// The MARRIAGE PROPOSAL pop-up (and the small "they answered" notices) — the same idea as the invitation pop-up (invitePopup.js), with a ring on it.
// Mounted once for the whole site: it appears on any page as soon as the player signs in, and again whenever a new proposal arrives
// (src/game/inviteManager.js polls the server).
//   • a waiting proposal: who proposes, "you would be his 3rd wife" (or "she would be your 3rd wife"), a live countdown, [Accept] [Decline] [Decide later] [Block]
//   • a proposal that ran out before the player signed in: told once that it was received and expired
// One pop-up at a time: while an invitation (or any other .inv-pop) is on screen, this one waits.

import './invites.css'
import { Events, on } from './eventBus.js'
import { attentionProposals, proposalActions, getInvites, refresh } from '../game/inviteManager.js'
import { getAccountInfo } from '../game/accountManager.js'
import { regNo, countdown } from './invitePopup.js'

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
export const ord = (n) => { const v = n % 100; return n + (['th', 'st', 'nd', 'rd'][(v - 20) % 10] || ['th', 'st', 'nd', 'rd'][v] || 'th') }
const when = (t) => new Date(t).toLocaleString(undefined, { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' })

export function mountProposalPopup() {
  const layer = document.createElement('div')
  layer.className = 'inv-layer'
  document.body.appendChild(layer)
  let showing = null
  let busy = false
  let clock = null
  let retry = null

  function toast(text) {
    const el = document.createElement('div'); el.className = 'inv-toast'; el.setAttribute('role', 'status'); el.textContent = text
    layer.appendChild(el)
    setTimeout(() => { el.classList.add('out'); setTimeout(() => el.remove(), 400) }, 7000)
  }
  function close() { clearInterval(clock); clock = null; showing = null; layer.querySelector('.inv-pop')?.remove(); next() }

  // wording depends on who is who: the proposer may be the husband (a man proposing) or the wife (a woman proposing)
  function words(p) {
    const iAmWife = p.wifeId === getAccountInfo().userId
    const rank = p.wifeRank ?? 1
    return { iAmWife, place: iAmWife ? `his ${ord(rank)} wife` : `your ${ord(rank)} wife`, heShe: iAmWife ? 'He' : 'She' }
  }

  function render(p, note = null) {
    clearInterval(clock)
    layer.querySelector('.inv-pop')?.remove()
    const more = attentionProposals().filter((x) => x.id !== p.id).length
    const expired = p.status === 'expired'
    const w = words(p), who = p.proposer
    const el = document.createElement('div')
    el.className = 'inv-pop'
    el.innerHTML = `
      <div class="inv-card" role="dialog" aria-modal="true" aria-labelledby="prp-title">
        <div class="inv-kicker">${expired ? 'Proposal expired' : 'A marriage proposal'}</div>
        <h2 id="prp-title">${expired ? `${esc(who.name)} proposed marriage, but the proposal has expired.` : `${esc(who.name)} proposes marriage.`}</h2>
        <p class="inv-from">No. ${regNo(who.registryNo)} · ${esc(who.kingdom ?? '')} · ${esc(who.title ?? '')}</p>
        ${note ? `<p class="inv-note inv-note--done" role="status">${esc(note)}</p>` : expired ? `<p>It was sent on ${esc(when(p.createdAt))} and ended on ${esc(when(p.expiresAt))}. ${esc(who.name)} can propose again.</p>` : `
        <dl class="inv-terms">
          <div><dt>Proposal ends in</dt><dd data-prp-left>${esc(countdown(p.expiresAt - Date.now()))}</dd></div>
          <div><dt>If you accept</dt><dd>${w.iAmWife ? 'You would be' : 'She would be'} ${esc(w.place)}</dd></div>
        </dl>
        <p class="inv-note">You both keep playing your own game — no coin moves between you. The Registry shows that you are married, and your combined wealth.</p>
        <p class="inv-note">${w.iAmWife ? `<b>Your husband will be able to see a report of you</b> — your net worth, recent activity, production and citizens.` : `You will get a <b>household report</b> on each wife: her net worth, recent activity and production.`}</p>`}
        <p class="inv-error" data-prp-error role="alert" hidden></p>
        <div class="inv-actions">
          ${note ? '<button type="button" class="inv-btn inv-btn--main" data-prp="close">Close</button>'
            : expired ? '<button type="button" class="inv-btn inv-btn--main" data-prp="ok">OK</button>'
            : `<button type="button" class="inv-btn inv-btn--main" data-prp="accept">Accept</button>
               <button type="button" class="inv-btn" data-prp="decline">Decline</button>
               <button type="button" class="inv-btn inv-btn--quiet" data-prp="later">Decide later</button>
               <button type="button" class="inv-btn inv-btn--quiet inv-btn--danger" data-prp="block">Block this player</button>`}
        </div>
        ${more ? `<p class="inv-more">${more} more proposal${more === 1 ? '' : 's'} waiting.</p>` : ''}
      </div>`
    layer.appendChild(el)
    el.querySelector('[data-prp]')?.focus()
    if (!expired && !note) clock = setInterval(() => { const l = el.querySelector('[data-prp-left]'); if (l) l.textContent = countdown(p.expiresAt - Date.now()); if (p.expiresAt <= Date.now()) { clearInterval(clock); refresh() } }, 1000)
  }

  function next() {
    if (showing) return
    if (!attentionProposals().length) return
    if (document.querySelector('.inv-pop')) { clearTimeout(retry); retry = setTimeout(next, 1200); return } // an invitation (or another dialog) is up: wait our turn
    showing = attentionProposals()[0]
    render(showing)
  }

  async function run(fn, doneNote) {
    if (busy || !showing) return
    busy = true
    const err = layer.querySelector('[data-prp-error]')
    try { const r = await fn(); if (doneNote) showing && render(showing, typeof doneNote === 'function' ? doneNote(r) : doneNote); else close() } catch (e) {
      const msg = e?.body?.error ?? 'Something went wrong — try again.'
      if (err) { err.textContent = msg; err.hidden = false }
      if (e?.status === 410 || e?.status === 409) { showing = null; setTimeout(() => { layer.querySelector('.inv-pop')?.remove(); next() }, 3500) }
    } finally { busy = false }
  }

  layer.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-prp]'); if (!b || !showing) return
    const p = showing
    switch (b.dataset.prp) {
      case 'accept': {
        const w = words(p)
        await run(() => proposalActions.accept(p.id), () => (w.iAmWife
          ? `You are married to ${p.proposer.name}. As his wife, you may like to know: your husband can now see a report of your net worth and activity.`
          : `You are married to ${p.proposer.name} — your ${ord(p.wifeRank ?? 1)} wife. Her report is part of your household report.`))
        break
      }
      case 'decline': run(() => proposalActions.decline(p.id), null); break
      case 'block': if (confirm(`Block ${p.proposer.name}? You will not receive proposals from them.`)) run(() => proposalActions.block(p.proposerId), null); break
      case 'later': await proposalActions.dismiss(p); close(); break
      case 'ok': await proposalActions.dismiss(p); close(); break
      case 'close': close(); break
      default:
    }
  })
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && showing && layer.querySelector('.inv-pop') && !layer.querySelector('.inv-note--done')) { const p = showing; proposalActions.dismiss(p); close() } })

  on(Events.InvitesChange, ({ proposalAttention: att, changedProposals }) => {
    for (const p of changedProposals ?? []) toast(p.status === 'accepted' ? `${p.targetName ?? 'The player'} accepted your marriage proposal — you are married.` : `${p.targetName ?? 'The player'} declined your marriage proposal.`)
    if (showing && !(att ?? []).some((x) => x.id === showing.id) && !layer.querySelector('.inv-note--done') && !busy) { showing = null; layer.querySelector('.inv-pop')?.remove() } // answered elsewhere / withdrawn / expired meanwhile
    if (!showing) next()
  })
  if (getInvites().loaded) next()
  return { toast }
}
